import React, {Fragment} from 'react';
import {usePolTranslation} from "v4/hooks/usePolTranslation";
import DashboardQuotesSummaryCell
    from "v4/pages/front/Dashboard/components/DashboardQuotesSummary/components/DashboardQuotesSummaryCell/DashboardQuotesSummaryCell";
import useDashboardQuotesSummary
    from "v4/pages/front/Dashboard/components/DashboardQuotesSummary/hooks/useDashboardQuotesSummary";
import {PolIcon} from "components/PolIcon/policon";
import ErrorBoundary from "components/Error/errorBoundary";
import ReactTooltip from "react-tooltip";
import useIsInView from "v4/hooks/useIsInView";
import DashboardTasksSummaryCell
    from "v4/pages/front/Dashboard/components/DashboardTasksSummary/components/DashboardTasksSummaryCell/DashboardTasksSummaryCell";
import {useFrontContext} from "v4/contexts/FrontContext";

export default function DashboardQuotesSummary() {
    const {t} = usePolTranslation();
    const {a11yValues} = useFrontContext();
    const [ref, isInView] = useIsInView();
    const {
        data,
        tableInfos,
        getPreSearch,
        previousMonth,
        nextMonth,
        resetDateInterval,
        state,
        toggleRefresh,
    } = useDashboardQuotesSummary(isInView);

    return (
        <ErrorBoundary canRetry>
            <div className="dashboard-quotes-summary" ref={ref}>
                <p>
                    {t('quotes_results')}
                    {state.isError && (
                        <>
                            <PolIcon icon="exclamation-circle"
                                     className="ml-2 icon-error"
                                     data-for="summary-quotes-error-tooltip"
                                     data-tip={t('server_error')}
                            />
                            <ReactTooltip id="summary-quotes-error-tooltip" effect="solid" wrapper="span"/>
                            <PolIcon icon="refresh"
                                     className="ml-2"
                                     style={{cursor: 'pointer'}}
                                     data-for="summary-quotes-refresh-tooltip"
                                     data-tip={t('retry')}
                                     onClick={toggleRefresh}
                            />
                            <ReactTooltip id="summary-quotes-refresh-tooltip" effect="solid" wrapper="span"/>
                        </>
                    )}
                </p>
                <table className={`${a11yValues?.contrasted_table ? 'pol-table--contrasted' : ''}`}>
                    <thead>
                    <tr>
                        <th width="180px"/>
                        <th colSpan={8}>{t('since_january')}</th>
                        <th colSpan={4}>{t('current_quotes')}</th>
                        {tableInfos?.remaining_months.length > 0 && (
                            <th colSpan={(tableInfos.remaining_months?.length * 2) ?? 12}>{t('half_quotes')}</th>
                        )}
                    </tr>
                    <tr>
                        <th/>
                        <th colSpan={2}>{t('issued_label')}</th>
                        <th colSpan={2}>{t('won_label')}</th>
                        <th colSpan={2}>{t('abandoned_label')}</th>
                        <th colSpan={2}>{t('lost_label')}</th>
                        <th colSpan={2}>{t('in_progress_label')}</th>
                        <th colSpan={2}>{t('late_label')}</th>
                        {tableInfos?.remaining_months && (
                            tableInfos.remaining_months.map(({
                                                                 label,
                                                                 yearNumber,
                                                                 monthNumber
                                                             }, remainingMonthsIndex) => {
                                const isFirstColumn = remainingMonthsIndex === 0;
                                const isLastColumn = remainingMonthsIndex === (tableInfos.remaining_months.length - 1);
                                const isCurrentMonth = (yearNumber === tableInfos.currentYear && monthNumber === tableInfos.currentMonth);

                                return (
                                    <th key={label}
                                        colSpan={2}
                                        className={`${isFirstColumn ? 'is-first-column' : ''} ${isCurrentMonth ? 'is-current-column' : ''} ${isLastColumn ? 'is-last-column' : ''}`}>
                                        {isFirstColumn && (
                                            <button className="quotes-summary__previous-btn"
                                                    disabled={!tableInfos.canPreviousMonth || state.isLoading}
                                                    onClick={previousMonth}
                                                    data-for="tooltip-dashboard-quotes-summary"
                                                    data-tip={tableInfos.canPreviousMonth ? t('previous_month') : ''}
                                            >
                                                <PolIcon icon="return"/>
                                            </button>
                                        )}
                                        {(isFirstColumn && !isCurrentMonth) && (
                                            <button className="quotes-summary__reset-btn"
                                                    disabled={state.isLoading}
                                                    onClick={resetDateInterval}
                                                    data-for="tooltip-dashboard-quotes-summary"
                                                    data-tip={t('reset_date')}
                                            >
                                                <PolIcon icon="refresh"/>
                                            </button>
                                        )}
                                        {label}
                                        {isLastColumn && (
                                            <>
                                                <button className="quotes-summary__next-btn"
                                                        disabled={!tableInfos.canNextMonth || state.isLoading}
                                                        onClick={nextMonth}
                                                        data-for="tooltip-dashboard-quotes-summary"
                                                        data-tip={tableInfos.canNextMonth ? t('next_month') : ''}
                                                >
                                                    <PolIcon icon="return" className="reverse-h"/>
                                                </button>
                                                <ReactTooltip id="tooltip-dashboard-quotes-summary" effect="solid"/>
                                            </>
                                        )}
                                    </th>
                                )
                            })
                        )}
                    </tr>
                    </thead>
                    <tbody>
                    {state.isLoading && !state.isError && !data && (
                        <tr>
                            {Array.from({length: 13 + (tableInfos?.remaining_months?.length ?? 0)}).map((_, index) => (
                                <DashboardTasksSummaryCell key={index} value={<PolIcon icon="circle-notch" className="spinner"/>}/>
                            ))}
                        </tr>
                    )}
                    {data && data.map((row, index) => (
                        <tr key={index}>
                            <DashboardQuotesSummaryCell value={row.fullname} className="ellipsis"/>
                            <DashboardQuotesSummaryCell value={row.total_issued}
                                                        isFinancial={true}
                                                        preSearch={getPreSearch('issued', index)}/>
                            <DashboardQuotesSummaryCell value={row.nb_issued}
                                                        preSearch={getPreSearch('issued', index)}/>
                            <DashboardQuotesSummaryCell value={row.total_won}
                                                        isFinancial={true}
                                                        preSearch={getPreSearch('won', index)}/>
                            <DashboardQuotesSummaryCell value={row.nb_won}
                                                        preSearch={getPreSearch('won', index)}/>
                            <DashboardQuotesSummaryCell value={row.total_abandonned}
                                                        isFinancial={true}
                                                        preSearch={getPreSearch('abandoned', index)}/>
                            <DashboardQuotesSummaryCell value={row.nb_abandonned}
                                                        preSearch={getPreSearch('abandoned', index)}/>
                            <DashboardQuotesSummaryCell value={row.total_lost}
                                                        isFinancial={true}
                                                        preSearch={getPreSearch('lost', index)}/>
                            <DashboardQuotesSummaryCell value={row.nb_lost}
                                                        preSearch={getPreSearch('lost', index)}/>
                            <DashboardQuotesSummaryCell value={row.total_in_progress}
                                                        isFinancial={true}
                                                        preSearch={getPreSearch('in_progress', index)}/>
                            <DashboardQuotesSummaryCell value={row.nb_in_progress}
                                                        preSearch={getPreSearch('in_progress', index)}/>
                            <DashboardQuotesSummaryCell value={row.total_late}
                                                        isFinancial={true}
                                                        preSearch={getPreSearch('late', index)}/>
                            <DashboardQuotesSummaryCell value={row.nb_late}
                                                        preSearch={getPreSearch('late', index)}/>
                            {tableInfos?.remaining_months && (
                                tableInfos.remaining_months.map(({yearNumber, monthNumber}, remainingMonthsIndex) => (
                                    <Fragment key={`${yearNumber}${monthNumber}`}>
                                        <DashboardQuotesSummaryCell
                                            isLoading={state.isLoading}
                                            value={row[`total_year_${yearNumber}_month_${monthNumber}`]}
                                            isFinancial={true}
                                            preSearch={getPreSearch(`total_year_${yearNumber}_month_${monthNumber}`, index)}
                                            className={`${remainingMonthsIndex === 0 ? 'is-first-column' : ''}`}/>
                                        <DashboardQuotesSummaryCell
                                            isLoading={state.isLoading}
                                            value={row[`nb_year_${yearNumber}_month_${monthNumber}`]}
                                            preSearch={getPreSearch(`total_year_${yearNumber}_month_${monthNumber}`, index)}
                                            className={`${remainingMonthsIndex === (tableInfos.remaining_months.length - 1) ? 'is-last-column' : ''}`}/>
                                    </Fragment>
                                ))
                            )}
                        </tr>
                    ))}
                    </tbody>
                    <tfoot>
                    <tr className="table-stat__body__total">
                        <td>{t('total_euro')}</td>
                        {
                            (state.isLoading && !state.isError && !data)
                                ? (
                                    Array.from({length: 12 + (tableInfos?.remaining_months?.length ?? 0) * 2}).map((_, index) => (
                                        <DashboardTasksSummaryCell
                                            key={index}
                                            value={<PolIcon icon="circle-notch" className="spinner"/>}/>
                                    ))
                                ) : (
                                    <>
                                        <DashboardQuotesSummaryCell value={tableInfos.totalByColumn.total_issued}
                                                                    isFinancial={true}
                                                                    preSearch={getPreSearch('total_issued')}/>
                                        <DashboardQuotesSummaryCell value={tableInfos.totalByColumn.nb_issued}
                                                                    preSearch={getPreSearch('total_issued')}/>
                                        <DashboardQuotesSummaryCell value={tableInfos.totalByColumn.total_won}
                                                                    isFinancial={true}
                                                                    preSearch={getPreSearch('total_won')}/>
                                        <DashboardQuotesSummaryCell value={tableInfos.totalByColumn.nb_won}
                                                                    preSearch={getPreSearch('total_won')}/>
                                        <DashboardQuotesSummaryCell value={tableInfos.totalByColumn.total_abandonned}
                                                                    isFinancial={true}
                                                                    preSearch={getPreSearch('total_abandoned')}/>
                                        <DashboardQuotesSummaryCell value={tableInfos.totalByColumn.nb_abandonned}
                                                                    preSearch={getPreSearch('total_abandoned')}/>
                                        <DashboardQuotesSummaryCell value={tableInfos.totalByColumn.total_lost}
                                                                    isFinancial={true}
                                                                    preSearch={getPreSearch('total_lost')}/>
                                        <DashboardQuotesSummaryCell value={tableInfos.totalByColumn.nb_lost}
                                                                    preSearch={getPreSearch('total_lost')}/>
                                        <DashboardQuotesSummaryCell value={tableInfos.totalByColumn.total_in_progress}
                                                                    isFinancial={true}
                                                                    preSearch={getPreSearch('total_in_progress')}/>
                                        <DashboardQuotesSummaryCell value={tableInfos.totalByColumn.nb_in_progress}
                                                                    preSearch={getPreSearch('total_in_progress')}/>
                                        <DashboardQuotesSummaryCell value={tableInfos.totalByColumn.total_late}
                                                                    isFinancial={true}
                                                                    preSearch={getPreSearch('total_late')}/>
                                        <DashboardQuotesSummaryCell value={tableInfos.totalByColumn.nb_late}
                                                                    preSearch={getPreSearch('total_late')}/>
                                        {tableInfos?.remaining_months && (
                                            tableInfos.remaining_months.map(({
                                                                                 yearNumber,
                                                                                 monthNumber
                                                                             }, remainingMonthsIndex) => (
                                                <Fragment key={`${yearNumber}${monthNumber}`}>
                                                    <DashboardQuotesSummaryCell
                                                        isLoading={state.isLoading}
                                                        value={tableInfos.totalByColumn[`total_year_${yearNumber}_month_${monthNumber}`]}
                                                        isFinancial={true}
                                                        preSearch={getPreSearch(`total_year_${yearNumber}_month_${monthNumber}`)}
                                                        className={`${remainingMonthsIndex === 0 ? 'is-first-column' : ''}`}/>
                                                    <DashboardQuotesSummaryCell
                                                        isLoading={state.isLoading}
                                                        value={tableInfos.totalByColumn[`nb_year_${yearNumber}_month_${monthNumber}`]}
                                                        preSearch={getPreSearch(`total_year_${yearNumber}_month_${monthNumber}`)}
                                                        className={`${remainingMonthsIndex === (tableInfos.remaining_months.length - 1) ? 'is-last-column' : ''}`}/>
                                                </Fragment>
                                            ))
                                        )}
                                    </>
                                )
                        }
                    </tr>
                    <tr className={'table-stat__body__total'}>
                        <td>{t('total_ponderate')}</td>
                        {
                            (state.isLoading && !state.isError && !data)
                                ? (
                                    Array.from({length: 12 + (tableInfos?.remaining_months?.length ?? 0) * 2}).map((_, index) => (
                                        <DashboardTasksSummaryCell
                                            key={index}
                                            value={<PolIcon icon="circle-notch" className="spinner"/>}/>
                                    ))
                                ) : (
                                    <>
                                        <DashboardQuotesSummaryCell value={tableInfos.totalByColumn.total_weighted_issued}
                                                                    isFinancial={true}/>
                                        <td/>
                                        <DashboardQuotesSummaryCell value={tableInfos.totalByColumn.total_weighted_won}
                                                                    isFinancial={true}/>
                                        <td/>
                                        <DashboardQuotesSummaryCell
                                            value={tableInfos.totalByColumn.total_weighted_abandonned}
                                            isFinancial={true}/>
                                        <td/>
                                        <DashboardQuotesSummaryCell value={tableInfos.totalByColumn.total_weighted_lost}
                                                                    isFinancial={true}/>
                                        <td/>
                                        <DashboardQuotesSummaryCell
                                            value={tableInfos.totalByColumn.total_weighted_in_progress}
                                            isFinancial={true}/>
                                        <td/>
                                        <DashboardQuotesSummaryCell value={tableInfos.totalByColumn.total_weighted_late}
                                                                    isFinancial={true}/>
                                        <td/>
                                        {tableInfos?.remaining_months && (
                                            tableInfos.remaining_months.map(({
                                                                                 yearNumber,
                                                                                 monthNumber
                                                                             }, remainingMonthsIndex) => (
                                                <Fragment key={`${yearNumber}${monthNumber}`}>
                                                    <DashboardQuotesSummaryCell
                                                        isLoading={state.isLoading}
                                                        value={tableInfos.totalByColumn[`total_weighted_year_${yearNumber}_month_${monthNumber}`]}
                                                        isFinancial={true}
                                                        className={`${remainingMonthsIndex === 0 ? 'is-first-column' : ''}`}/>
                                                    <td className={`${remainingMonthsIndex === (tableInfos.remaining_months.length - 1) ? 'is-last-column' : ''}`}/>
                                                </Fragment>
                                            ))
                                        )}
                                    </>
                                )
                        }
                    </tr>
                    </tfoot>
                </table>
            </div>
        </ErrorBoundary>
    )
}
